import React, { useState } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../context/AuthContext'
import { useQRScanner } from '../../hooks/useQRScanner'
import QRScannerModal from '../../components/admin/QRScannerModal'
import ManualQRInput from '../../components/admin/ManualQRInput'
import QRValidationResult from '../../components/admin/QRValidationResult'

const QRScannerPage: React.FC = () => {
  const { tienePermiso } = useAuth()
  const { colors } = useTheme()
  const { isValidating, validationResult, validateQR, clearResult } = useQRScanner()
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const [isManualInputOpen, setIsManualInputOpen] = useState(false)

  const handleScanSuccess = async (qrData: string) => {
    setIsScannerOpen(false)
    await validateQR(qrData)
  }

  const handleManualInput = async (qrData: string) => {
    setIsManualInputOpen(false)
    await validateQR(qrData)
  }

  const handleCloseScannerModal = () => {
    setIsScannerOpen(false)
  }

  const handleCloseManualInput = () => {
    setIsManualInputOpen(false)
  }

  const handleCloseResult = () => {
    clearResult()
  }

  // Verificar permiso
  if (!tienePermiso('qr.validar')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h2>
          <p className="text-gray-600">
            No tienes permiso para acceder a esta funcionalidad (qr.validar).
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            📱 Validador de Reservas
          </h1>
          <p className="text-lg text-gray-600">
            Escanea el código QR de las reservas de los huéspedes para validarlas en la entrada del camping.
          </p>
        </div>

        {/* Tarjetas de información */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Cómo funciona */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-3xl mb-3">📷</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              1. Escanear QR
            </h3>
            <p className="text-gray-600 text-sm">
              Presiona el botón para abrir la cámara y apunta al código QR del huésped.
            </p>
          </div>

          {/* Validación */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-3xl mb-3">✅</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              2. Validar Automáticamente
            </h3>
            <p className="text-gray-600 text-sm">
              El sistema verifica que la reserva sea válida y esté confirmada.
            </p>
          </div>

          {/* Resultado */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-3xl mb-3">🎉</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              3. Confirmar Entrada
            </h3>
            <p className="text-gray-600 text-sm">
              Verás los datos del huésped y podrás confirmar su entrada.
            </p>
          </div>
        </div>

        {/* Botones de escaneo e ingreso manual */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          <button
            onClick={() => setIsScannerOpen(true)}
            className="px-8 py-4 rounded-lg font-semibold text-white text-lg transition-all duration-200 hover:shadow-lg"
            style={{
              backgroundColor: colors.primary,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            📱 Abrir Escáner QR
          </button>
          <button
            onClick={() => setIsManualInputOpen(true)}
            className="px-8 py-4 rounded-lg font-semibold text-white text-lg transition-all duration-200 hover:shadow-lg"
            style={{
              backgroundColor: colors.primaryDark,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9'
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            ✏️ Ingreso Manual
          </button>
        </div>

        {/* Información adicional */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            💡 Información Importante
          </h3>
          <ul className="text-blue-800 space-y-2 text-sm">
            <li>✓ El código QR está incluido en el correo de confirmación de la reserva</li>
            <li>✓ También está disponible en la página de confirmación del sitio web</li>
            <li>✓ Solo se validan reservas confirmadas con fechas vigentes</li>
            <li>✓ El sistema verifica automáticamente los datos en la base de datos</li>
            <li>✓ Puedes ingresar manualmente el código si tienes problemas con la cámara</li>
          </ul>
        </div>

        {/* Última validación */}
        {validationResult && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Última Validación
            </h3>
            <div
              className="p-6 rounded-lg"
              style={{
                backgroundColor: validationResult.valid ? '#D1FAE5' : '#FEE2E2',
                borderLeft: `4px solid ${validationResult.valid ? '#059669' : '#DC2626'}`,
              }}
            >
              <p className="font-semibold text-gray-900 mb-2">
                {validationResult.valid ? '✅ Válida' : '❌ Inválida'}
              </p>
              <p className="text-gray-700 text-sm">
                {validationResult.message}
              </p>
              {validationResult.valid && validationResult.cliente_nombre && (
                <>
                  <p className="text-gray-700 text-sm mt-3">
                    <strong>Cliente:</strong> {validationResult.cliente_nombre}
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Espacio:</strong> {validationResult.espacio_nombre}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modales */}
      <QRScannerModal
        isOpen={isScannerOpen}
        onClose={handleCloseScannerModal}
        onScanSuccess={handleScanSuccess}
      />

      <ManualQRInput
        isOpen={isManualInputOpen}
        onClose={handleCloseManualInput}
        onSubmit={handleManualInput}
        isLoading={isValidating}
      />

      <QRValidationResult
        data={validationResult}
        isLoading={isValidating}
        onClose={handleCloseResult}
      />
    </div>
  )
}

export default QRScannerPage
